import { useState } from 'react';
import { ClbButton, ClbIcon, ClbTooltip } from '@celebration/react';
import IOfferCardDetailsProps from './IOfferCardDetailsProps';
import './offer-card-details.scss';
import i18n from '../../../../../lib/i18n';
import IconInfo from '../../../../../common/components/icon-info';
import {
    getAddressFormatted,
    getStringValueOrNoInfoText,
} from '../../../../../utils/string-utils';
import { formatCurrency } from '../../../../../utils/currency-utils';
import LanguageContext from '../../../../../app/contexts/language';
import { getUTCDate } from '../../../../../utils/date-utils';
import CopyInfo from '../../../../../common/components/copy-info';
import { useOffersRound } from '../../../../../app/contexts/offers-round/offers-round.context';
import { getAcceptedRateByVehicle } from '../../utils/offers-utils';

export default function OfferCardDetails({
    offer,
}: Readonly<IOfferCardDetailsProps>) {
    const { roundState, acceptedOffersRates } = useOffersRound();
    const { language } = LanguageContext.useLanguage();

    const [isDetailOpened, setIsDetailOpened] = useState<boolean>(false);

    const { loadId, route, dates, shipment, vehicle, channel } = offer.load;
    const acceptedRate = offer.accepted
        ? (acceptedOffersRates[offer.loadOfferId] ??
          getAcceptedRateByVehicle(offer))
        : undefined;

    return (
        <>
            <ClbTooltip
                description={i18n.t('Info')}
                position="top"
                showArrow
                colorMode="dark"
            >
                <ClbButton
                    icon="info"
                    size="md"
                    styleType="icon"
                    data-testid="offer-footer-details-button"
                    onClick={() => setIsDetailOpened(true)}
                />
            </ClbTooltip>

            {isDetailOpened && (
                <div
                    className="offer-details"
                    data-testid="offer-footer-details-modal"
                >
                    <div className="offer-details-modal">
                        <div className="offer-details-header">
                            <ClbIcon
                                icon="close"
                                size="md"
                                onClick={() => setIsDetailOpened(false)}
                                data-testid="offer-details-close-button"
                            />
                        </div>
                        <div className="offer-details-body">
                            <h2>{i18n.t('Detalhes')}</h2>

                            <hr />

                            {roundState.currentPhase === 'Confirmation' && (
                                <CopyInfo
                                    title={i18n.t('Load')}
                                    info={loadId}
                                />
                            )}

                            {roundState.currentPhase === 'Confirmation' && (
                                <CopyInfo
                                    title={i18n.t('Pedido')}
                                    info={shipment.shipmentNumber}
                                />
                            )}

                            {roundState.currentPhase === 'Confirmation' &&
                                shipment.freightOrder && (
                                    <CopyInfo
                                        title="Freight Order"
                                        info={shipment.freightOrder}
                                    />
                                )}

                            <IconInfo
                                icon="location"
                                title={i18n.t('Origem')}
                                info={getAddressFormatted(route.source)}
                            />

                            <IconInfo
                                icon="map-pin-icon"
                                title={i18n.t('Destino')}
                                info={getAddressFormatted(route.destination)}
                            />

                            <IconInfo
                                icon="calendar"
                                title={i18n.t('Date')}
                                info={
                                    dates.collectionDate &&
                                    getUTCDate(
                                        dates.collectionDate,
                                    )?.toLocaleDateString()
                                }
                            />

                            <IconInfo
                                icon="shopping-cart"
                                title={i18n.t('Carga')}
                                info={shipment.material.name}
                            />

                            <IconInfo
                                icon="truck-icon"
                                title={i18n.t('CaminhaoSugerido')}
                                info={vehicle.description}
                            />

                            {acceptedRate?.vehicle.description && (
                                <IconInfo
                                    icon="truck-icon"
                                    title={i18n.t('CaminhaoEscolhido')}
                                    info={acceptedRate?.vehicle.description}
                                />
                            )}

                            <div className="offer-details-footer">
                                <div className="offer-details-footer-attribute">
                                    {getStringValueOrNoInfoText(
                                        i18n.t(channel),
                                    )}
                                </div>
                                <div className="offer-details-footer-attribute">
                                    {`${shipment.weight} ${shipment.weightUnitMeasure}`}
                                </div>
                                <div className="offer-details-footer-attribute">
                                    {formatCurrency(
                                        acceptedRate?.chargeAmount ??
                                            shipment.chargeAmount,
                                        shipment.currency,
                                        language,
                                    )}
                                </div>
                                {Boolean(shipment.skids) &&
                                    shipment.skids > 0 && (
                                        <div className="offer-details-footer-attribute">
                                            {shipment.skids} Plts
                                        </div>
                                    )}
                                {offer.load.isReturnable && (
                                    <div
                                        className={
                                            'offer-details-footer-attribute ' +
                                            'offer-details-footer-attribute--returnable'
                                        }
                                        data-testid="offer-details-footer-attribute__returnable"
                                    >
                                        {i18n.t('Returnable')}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
